import { useState, useEffect, useCallback } from 'react';
import { Building2, Download, Calendar, Filter, FileText, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { logger } from '../utils/logger';
import { Button } from './ui/button';

/** Raw CSV/Qargo fields read off an API order (via its `_raw` payload). */
interface RawOrderFields {
  order_id?: string;
  _order_id?: string;
  name?: string;
  customer?: string;
  customer_name?: string;
  customer_id?: string;
  date?: string;
  status?: string;
  co2e_well_to_wheel?: string | number;
  co2e_tank_to_wheel?: string | number;
  co2e_well_to_tank?: string | number;
  metrics_distance_total?: string | number;
  origin_name?: string;
  destination_name?: string;
  origin_city?: string;
  destination_city?: string;
  transport_service?: string;
}

/** Order row as returned by the backend orders endpoint. */
interface ApiOrder {
  _raw?: RawOrderFields;
  id?: string;
  name?: string;
  customerRef?: string;
  date?: string;
  status?: string;
  pickup?: { name?: string };
  delivery?: { name?: string };
}

interface Order {
  order_id?: string;
  name?: string;
  customer?: string;
  customer_id?: string;
  date?: string;
  status?: string;
  co2e_well_to_wheel?: number;
  co2e_tank_to_wheel?: number;
  co2e_well_to_tank?: number;
  metrics_distance_total?: number;
  metrics_distance_loaded?: number;
  metrics_distance_empty?: number;
  origin_name?: string;
  destination_name?: string;
  origin_city?: string;
  destination_city?: string;
  transport_service?: string;
  [key: string]: unknown;
}

interface ClientCarbonData {
  totalEmissions: number;
  totalOrders: number;
  totalDistance: number;
  byClient: Array<{
    client: string;
    clientId: string;
    emissions: number;
    orders: number;
    distance: number;
  }>;
  byJob: Array<{
    jobId: string;
    jobName: string;
    client: string;
    emissions: number;
    distance: number;
    date: string;
  }>;
}

type TimePeriod = 'today' | 'yesterday' | 'lastWeek' | 'lastMonth' | 'custom';

export default function ClientCarbonReporting() {
  // Default to lastMonth to show data (CSV has dates from Jan 2026)
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('lastMonth');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [selectedJob, setSelectedJob] = useState<string>('all');
  const [orders, setOrders] = useState<Order[]>([]);
  const [clients, setClients] = useState<string[]>([]);
  const [jobs, setJobs] = useState<Array<{ id: string; name: string; client: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [carbonData, setCarbonData] = useState<ClientCarbonData | null>(null);
  const [clientPage, setClientPage] = useState(1);
  const [clientRowsPerPage, setClientRowsPerPage] = useState(10);
  const [jobPage, setJobPage] = useState(1);
  const [jobRowsPerPage, setJobRowsPerPage] = useState(10);

  // Calculate date range based on time period
  const getDateRange = useCallback((): { start: string; end: string } => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let start: Date;
    let end: Date = new Date(today);
    end.setHours(23, 59, 59, 999);

    switch (timePeriod) {
      case 'today':
        start = new Date(today);
        break;
      case 'yesterday':
        start = new Date(today);
        start.setDate(start.getDate() - 1);
        end = new Date(start);
        end.setHours(23, 59, 59, 999);
        break;
      case 'lastWeek':
        start = new Date(today);
        start.setDate(start.getDate() - 7);
        break;
      case 'lastMonth':
        start = new Date(today);
        start.setMonth(start.getMonth() - 1);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          return {
            start: customStartDate,
            end: customEndDate
          };
        }
        // Fallback to today if custom dates not set
        start = new Date(today);
        end = new Date(today);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        start = new Date(today);
    }

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  }, [timePeriod, customStartDate, customEndDate]);

  // Calculate carbon emissions from orders
  const calculateCarbonData = useCallback((ordersList: Order[], clientFilter?: string, jobFilter?: string) => {
    // Filter by selected client (using 'customer' field)
    let filteredOrders = ordersList;
    if (clientFilter && clientFilter !== 'all') {
      filteredOrders = ordersList.filter((o: Order) => {
        const customer = o['customer'] || o.customer || '';
        return customer === clientFilter;
      });
    }

    // Filter by selected job (using order_id)
    if (jobFilter && jobFilter !== 'all') {
      filteredOrders = filteredOrders.filter((o: Order) => {
        const orderId = o['order_id'] || o.order_id || '';
        return orderId === jobFilter;
      });
    }

    // Calculate totals
    let totalEmissions = 0;
    let totalDistance = 0;

    // Group by client
    const clientMap = new Map<string, { emissions: number; orders: number; distance: number; clientId: string }>();
    
    // Group by job
    const jobData: Array<{
      jobId: string;
      jobName: string;
      client: string;
      emissions: number;
      distance: number;
      date: string;
    }> = [];

    filteredOrders.forEach((order: Order) => {
      // Access fields using bracket notation since CSV headers are lowercase
      // Try both bracket notation and direct property access
      const co2eWellToWheel = order['co2e_well_to_wheel'] !== undefined && order['co2e_well_to_wheel'] !== null
        ? (typeof order['co2e_well_to_wheel'] === 'number' ? order['co2e_well_to_wheel'] : parseFloat(String(order['co2e_well_to_wheel'])))
        : (order.co2e_well_to_wheel !== undefined && order.co2e_well_to_wheel !== null
          ? (typeof order.co2e_well_to_wheel === 'number' ? order.co2e_well_to_wheel : parseFloat(String(order.co2e_well_to_wheel)))
          : undefined);

      const co2eTankToWheel = order['co2e_tank_to_wheel'] !== undefined && order['co2e_tank_to_wheel'] !== null
        ? (typeof order['co2e_tank_to_wheel'] === 'number' ? order['co2e_tank_to_wheel'] : parseFloat(String(order['co2e_tank_to_wheel'])))
        : (order.co2e_tank_to_wheel !== undefined && order.co2e_tank_to_wheel !== null
          ? (typeof order.co2e_tank_to_wheel === 'number' ? order.co2e_tank_to_wheel : parseFloat(String(order.co2e_tank_to_wheel)))
          : undefined);

      const co2eWellToTank = order['co2e_well_to_tank'] !== undefined && order['co2e_well_to_tank'] !== null
        ? (typeof order['co2e_well_to_tank'] === 'number' ? order['co2e_well_to_tank'] : parseFloat(String(order['co2e_well_to_tank'])))
        : (order.co2e_well_to_tank !== undefined && order.co2e_well_to_tank !== null
          ? (typeof order.co2e_well_to_tank === 'number' ? order.co2e_well_to_tank : parseFloat(String(order.co2e_well_to_tank)))
          : undefined);

      const distanceTotal = order['metrics_distance_total'] !== undefined && order['metrics_distance_total'] !== null
        ? (typeof order['metrics_distance_total'] === 'number' ? order['metrics_distance_total'] : parseFloat(String(order['metrics_distance_total'])))
        : (order.metrics_distance_total !== undefined && order.metrics_distance_total !== null
          ? (typeof order.metrics_distance_total === 'number' ? order.metrics_distance_total : parseFloat(String(order.metrics_distance_total)))
          : undefined);
      
      // Use well-to-wheel if available (most comprehensive - includes fuel production)
      // Fallback to tank-to-wheel if well-to-wheel not available
      // Final fallback: calculate from distance using standard emission factor
      let emissions = 0;
      if (co2eWellToWheel !== undefined && !isNaN(co2eWellToWheel) && co2eWellToWheel > 0) {
        emissions = co2eWellToWheel;
      } else if (co2eTankToWheel !== undefined && !isNaN(co2eTankToWheel) && co2eTankToWheel > 0) {
        // If only tank-to-wheel is available, add well-to-tank estimate (typically ~20% of tank-to-wheel)
        const wellToTank = co2eWellToTank !== undefined && !isNaN(co2eWellToTank) && co2eWellToTank > 0 ? co2eWellToTank : co2eTankToWheel * 0.2;
        emissions = co2eTankToWheel + wellToTank;
      } else if (distanceTotal !== undefined && !isNaN(distanceTotal) && distanceTotal > 0) {
        // Fallback: calculate from distance
        // Standard UK emission factor: ~0.206 kg CO2e per km for HGV (well-to-wheel)
        const distanceKm = distanceTotal / 1000;
        emissions = distanceKm * 0.206;
      }
      
      const distance = distanceTotal !== undefined && !isNaN(distanceTotal) && distanceTotal > 0 ? distanceTotal / 1000 : 0; // Convert meters to km
      
      totalEmissions += emissions;
      totalDistance += distance;

      // Use 'customer' field (same as Orders tab)
      const client = order['customer'] || order.customer || 'Unknown';
      const clientId = order['customer_id'] || order.customer_id || client;

      // Update client totals
      if (!clientMap.has(client)) {
        clientMap.set(client, { emissions: 0, orders: 0, distance: 0, clientId });
      }
      const clientData = clientMap.get(client)!;
      clientData.emissions += emissions;
      clientData.orders += 1;
      clientData.distance += distance;

      // Add job data using order_id
      const orderId = order['order_id'] || order.order_id || '';
      const orderName = order['name'] || order.name || '';
      jobData.push({
        jobId: orderId,
        jobName: orderName || orderId || 'Unknown',
        client,
        emissions,
        distance,
        date: order['date'] || order.date || ''
      });
    });

    const byClient = Array.from(clientMap.entries()).map(([client, data]) => ({
      client,
      clientId: data.clientId,
      emissions: data.emissions,
      orders: data.orders,
      distance: data.distance
    })).sort((a, b) => b.emissions - a.emissions);

    const carbonDataResult = {
      totalEmissions,
      totalOrders: filteredOrders.length,
      totalDistance,
      byClient,
      byJob: jobData.sort((a, b) => b.emissions - a.emissions)
    };
    
    console.log('[ClientCarbonReporting] Setting carbon data:', {
      totalEmissions,
      totalOrders: filteredOrders.length,
      totalDistance,
      clientsCount: byClient.length,
      jobsCount: jobData.length
    });
    
    setCarbonData(carbonDataResult);
  }, []);

  // Fetch orders from the API endpoint (which finds latest file from S3)
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Use the same endpoint as Orders tab - it finds the latest Qargo Results from S3
      const url = API_ENDPOINTS.qargoOrders();
      
      console.log('[ClientCarbonReporting] Fetching orders from API:', url);
      logger.info('Fetching orders from Qargo API endpoint:', url);

      const response = await authenticatedFetch(url);
      console.log('[ClientCarbonReporting] Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[ClientCarbonReporting] Failed to fetch orders:', response.status, errorData);
        throw new Error(errorData.error || errorData.message || `Failed to fetch orders: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const apiOrders = Array.isArray(data) ? data : (data.orders || []);
      
      console.log('[ClientCarbonReporting] Received', apiOrders.length, 'orders from API');
      logger.info(`Received ${apiOrders.length} orders from API`);

      if (apiOrders.length === 0) {
        throw new Error('No orders found in the latest Qargo export');
      }

      // Map API orders to our Order interface using _raw data
      const ordersList: Order[] = apiOrders.map((apiOrder: ApiOrder) => {
        // Access raw CSV fields from _raw property
        const raw = (apiOrder._raw || apiOrder) as RawOrderFields;
        
        // Extract fields from raw data (CSV columns are lowercase)
        const orderId = raw.order_id || raw._order_id || apiOrder.id || '';
        const orderName = raw.name || apiOrder.name || orderId;
        const customer = raw.customer || raw.customer_name || apiOrder.customerRef || '';
        const customerId = raw.customer_id || '';
        const orderDate = raw.date || apiOrder.date || '';
        const orderStatus = raw.status || apiOrder.status || '';
        
        // Parse numeric fields
        const co2eWellToWheel = raw.co2e_well_to_wheel && raw.co2e_well_to_wheel !== '' 
          ? parseFloat(String(raw.co2e_well_to_wheel)) 
          : undefined;
        const co2eTankToWheel = raw.co2e_tank_to_wheel && raw.co2e_tank_to_wheel !== '' 
          ? parseFloat(String(raw.co2e_tank_to_wheel)) 
          : undefined;
        const co2eWellToTank = raw.co2e_well_to_tank && raw.co2e_well_to_tank !== '' 
          ? parseFloat(String(raw.co2e_well_to_tank)) 
          : undefined;
        const metricsDistanceTotal = raw.metrics_distance_total && raw.metrics_distance_total !== '' 
          ? parseFloat(String(raw.metrics_distance_total)) 
          : undefined;
        
        return {
          order_id: orderId,
          name: orderName,
          customer: customer,
          customer_id: customerId,
          date: orderDate,
          status: orderStatus,
          co2e_well_to_wheel: co2eWellToWheel,
          co2e_tank_to_wheel: co2eTankToWheel,
          co2e_well_to_tank: co2eWellToTank,
          metrics_distance_total: metricsDistanceTotal,
          origin_name: raw.origin_name || apiOrder.pickup?.name || '',
          destination_name: raw.destination_name || apiOrder.delivery?.name || '',
          origin_city: raw.origin_city || '',
          destination_city: raw.destination_city || '',
          transport_service: raw.transport_service || '',
          // Keep raw data for debugging
          _raw: raw
        };
      });

      console.log('[ClientCarbonReporting] Mapped', ordersList.length, 'orders');
      if (ordersList.length > 0) {
        const sample = ordersList[0];
        console.log('[ClientCarbonReporting] Sample order (first):', {
          order_id: sample.order_id,
          name: sample.name,
          customer: sample.customer,
          date: sample.date,
          status: sample.status,
          co2e_well_to_wheel: sample.co2e_well_to_wheel,
          co2e_tank_to_wheel: sample.co2e_tank_to_wheel,
          metrics_distance_total: sample.metrics_distance_total
        });
      }
      logger.info(`Mapped ${ordersList.length} orders from API response`);

      // Filter by date range if specified (but don't filter if no date range or if it would exclude all data)
      const { start, end } = getDateRange();
      console.log('[ClientCarbonReporting] Date range filter:', start, 'to', end);
      let filteredOrders = ordersList;
      
      // Only apply date filter if we have a valid date range AND it's not "all-time"
      // For "today", "yesterday", etc., apply the filter
      // But be lenient - if no orders match, show a warning but don't filter
      if (start && end && timePeriod !== 'custom') {
        const beforeFilter = filteredOrders.length;
        filteredOrders = ordersList.filter((order: Order) => {
          const orderDate = order['date'] || order.date || '';
          if (!orderDate) return true; // Include if no date available
          // Extract just the date part (YYYY-MM-DD) for comparison
          const orderDateStr = orderDate.toString().split('T')[0].split(' ')[0];
          const matches = orderDateStr >= start && orderDateStr <= end;
          return matches;
        });
        console.log('[ClientCarbonReporting] After date filtering:', filteredOrders.length, 'orders (from', beforeFilter, ')');
        
        // If date filtering removed all orders, show all orders instead with a warning
        if (filteredOrders.length === 0 && beforeFilter > 0) {
          console.warn('[ClientCarbonReporting] Date filter removed all orders, showing all orders instead');
          filteredOrders = ordersList; // Show all orders if date filter is too restrictive
        }
        
        logger.info(`After date filtering (${start} to ${end}): ${filteredOrders.length} orders`);
      } else if (timePeriod === 'custom' && customStartDate && customEndDate) {
        // Apply custom date filter
        filteredOrders = ordersList.filter((order: Order) => {
          const orderDate = order['date'] || order.date || '';
          if (!orderDate) return true;
          const orderDateStr = orderDate.toString().split('T')[0].split(' ')[0];
          return orderDateStr >= customStartDate && orderDateStr <= customEndDate;
        });
        console.log('[ClientCarbonReporting] After custom date filtering:', filteredOrders.length, 'orders');
      }

      // Filter out cancelled orders
      const validOrders = filteredOrders.filter((order: Order) => {
        const status = (order.status || order['status'] || '').toLowerCase();
        return status !== 'cancelled' && status !== 'dont_invoice';
      });

      logger.info(`After filtering cancelled orders: ${validOrders.length} valid orders`);

      setOrders(validOrders);

      // Extract unique clients from 'customer' field (same as Orders tab uses customerRef)
      const uniqueClients = Array.from(
        new Set(
          validOrders
            .map((o: Order) => {
              // Use 'customer' field first (matches Orders tab), fallback to customer_id
              const customer = o['customer'] || o.customer || '';
              return customer.trim();
            })
            .filter(Boolean)
        )
      ).sort() as string[];
      setClients(uniqueClients);
      console.log('[ClientCarbonReporting] Found', uniqueClients.length, 'distinct clients:', uniqueClients.slice(0, 10));
      logger.info(`Found ${uniqueClients.length} distinct clients:`, uniqueClients.slice(0, 10));

      // Extract unique jobs using order_id (as requested)
      const uniqueJobs = validOrders.map((o: Order) => {
        const orderId = o['order_id'] || o.order_id || '';
        const customer = o['customer'] || o.customer || '';
        const orderName = o['name'] || o.name || '';
        return {
          id: orderId,
          name: orderName || orderId || 'Unknown',
          client: customer || 'Unknown'
        };
      }).filter(j => j.id); // Only include jobs with valid order_id
      setJobs(uniqueJobs);
      logger.info(`Found ${uniqueJobs.length} unique jobs (using order_id)`);

      // Calculate carbon data with current filters (will be recalculated when filters change)
      console.log('[ClientCarbonReporting] About to calculate carbon data for', validOrders.length, 'orders');
      calculateCarbonData(validOrders, selectedClient, selectedJob);
      console.log('[ClientCarbonReporting] Carbon data calculation complete');

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch orders';
      console.error('[ClientCarbonReporting] Error:', err);
      setError(message);
      logger.error('Error fetching orders:', err);
      // Reset data on error
      setOrders([]);
      setClients([]);
      setJobs([]);
      setCarbonData(null);
    } finally {
      setLoading(false);
      console.log('[ClientCarbonReporting] Fetch complete, loading set to false');
    }
  }, [getDateRange, calculateCarbonData]);


  // Recalculate when filters change (without refetching orders)
  useEffect(() => {
    if (orders.length > 0) {
      console.log('[ClientCarbonReporting] Recalculating carbon data with filters:', {
        ordersCount: orders.length,
        selectedClient,
        selectedJob
      });
      calculateCarbonData(orders, selectedClient, selectedJob);
      // Reset pagination when filters change
      setClientPage(1);
      setJobPage(1);
    }
  }, [selectedClient, selectedJob, orders.length, calculateCarbonData]);

  // Fetch orders when time period changes or on mount
  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timePeriod, customStartDate, customEndDate]);

  // Download report as CSV
  const downloadReport = () => {
    if (!carbonData) return;

    const headers = [
      'Client',
      'Client ID',
      'Job ID',
      'Job Name',
      'Date',
      'Emissions (kg CO2e)',
      'Distance (km)',
      'Origin',
      'Destination',
      'Transport Service'
    ];

    const rows = carbonData.byJob.map(job => {
      const order = orders.find(o => {
        const orderId = o['order_id'] || o.order_id || '';
        return orderId === job.jobId;
      });
      return [
        job.client,
        order?.['customer_id'] || order?.customer_id || '',
        job.jobId, // Using order_id as requested
        job.jobName,
        job.date,
        job.emissions.toFixed(2),
        job.distance.toFixed(2),
        order?.['origin_name'] || order?.origin_name || order?.['origin_city'] || order?.origin_city || '',
        order?.['destination_name'] || order?.destination_name || order?.['destination_city'] || order?.destination_city || '',
        order?.['transport_service'] || order?.transport_service || ''
      ].map(val => `"${val}"`).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `client_carbon_report_${getDateRange().start}_to_${getDateRange().end}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Debug logging
  useEffect(() => {
    logger.info('ClientCarbonReporting component mounted/updated', {
      timePeriod,
      customStartDate,
      customEndDate,
      selectedClient,
      selectedJob,
      ordersCount: orders.length,
      clientsCount: clients.length,
      jobsCount: jobs.length,
      hasCarbonData: !!carbonData,
      loading,
      error
    });
  }, [timePeriod, customStartDate, customEndDate, selectedClient, selectedJob, orders.length, clients.length, jobs.length, carbonData, loading, error]);

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header with gradient background */}
      <div className="relative overflow-hidden bg-gradient-to-br from-success/30 via-card to-background rounded-xl border border-success/20 p-6 shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-r from-success/5 to-transparent"></div>
        <div className="relative flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <Building2 className="w-8 h-8 text-success drop-shadow-glow-success-lg" />
              Client Carbon Reporting
            </h2>
            <p className="text-foreground text-sm">
              View carbon emissions by client and job based on orders completed with the company.
            </p>
            <p className="text-muted-foreground text-xs mt-2 italic">
              Source of data: Qargo
            </p>
          </div>
        </div>
      </div>

      {/* Filters with modern design */}
      <div className="bg-gradient-to-br from-card to-background rounded-xl border border-border/50 p-6 space-y-4 shadow-xl backdrop-blur-sm">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-success" />
          <h3 className="text-lg font-semibold text-white">Filters & Controls</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Time Period Selector */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Time Period
            </label>
            <select
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
              className="w-full px-4 py-2.5 bg-secondary/80 text-white rounded-lg border border-accent/50 focus:outline-none focus:ring-2 focus:ring-success/50 focus:border-success/50 transition-all hover:bg-secondary hover:border-accent cursor-pointer backdrop-blur-sm"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="lastWeek">Last Week</option>
              <option value="lastMonth">Last Month</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {/* Custom Date Range */}
          {timePeriod === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-white mb-2">Start Date</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-4 py-2.5 bg-secondary/80 text-white rounded-lg border border-accent/50 focus:outline-none focus:ring-2 focus:ring-success/50 focus:border-success/50 transition-all hover:bg-secondary hover:border-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">End Date</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-4 py-2.5 bg-secondary/80 text-white rounded-lg border border-accent/50 focus:outline-none focus:ring-2 focus:ring-success/50 focus:border-success/50 transition-all hover:bg-secondary hover:border-accent"
                />
              </div>
            </>
          )}

          {/* Client Selector */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              <Building2 className="w-4 h-4 inline mr-1" />
              Client
            </label>
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="w-full px-4 py-2.5 bg-secondary/80 text-white rounded-lg border border-accent/50 focus:outline-none focus:ring-2 focus:ring-success/50 focus:border-success/50 transition-all hover:bg-secondary hover:border-accent cursor-pointer backdrop-blur-sm"
            >
              <option value="all">All Clients</option>
              {clients.map(client => (
                <option key={client} value={client}>{client}</option>
              ))}
            </select>
          </div>

          {/* Job Selector */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Job
            </label>
            <select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              className="w-full px-4 py-2.5 bg-secondary/80 text-white rounded-lg border border-accent/50 focus:outline-none focus:ring-2 focus:ring-success/50 focus:border-success/50 transition-all hover:bg-secondary hover:border-accent cursor-pointer backdrop-blur-sm"
            >
              <option value="all">All Jobs</option>
              {jobs
                .filter(job => selectedClient === 'all' || job.client === selectedClient)
                .map(job => (
                  <option key={job.id} value={job.id}>
                    {job.name} ({job.client})
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* Refresh Button */}
        <div className="flex justify-end">
          <Button
            type="button"
            variant="default"
            size="lg"
            onClick={fetchOrders}
            disabled={loading}
            className="shadow-lg hover:shadow-success/50 transform hover:scale-105 active:scale-95"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <Filter className="w-4 h-4" />
                Refresh Data
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-gradient-to-r from-destructive/30 to-destructive/20 border border-destructive/50 rounded-xl p-5 flex items-center gap-3 text-destructive shadow-lg backdrop-blur-sm animate-fadeIn">
          <AlertCircle className="w-6 h-6 text-destructive flex-shrink-0" />
          <div>
            <div className="font-semibold text-destructive mb-1">Error Loading Data</div>
            <span className="text-sm text-destructive/80">{error}</span>
          </div>
        </div>
      )}

      {/* Summary Cards with beautiful design */}
      {carbonData && !loading && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="relative overflow-hidden bg-gradient-to-br from-destructive/20 via-card to-background rounded-xl border border-destructive/30 p-6 shadow-xl hover:shadow-destructive/20 transition-all duration-300 transform hover:scale-105">
              <div className="absolute top-0 right-0 w-32 h-32 bg-destructive/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-destructive rounded-full animate-pulse"></div>
                  <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Total Emissions</div>
                </div>
                <div className="text-4xl font-bold text-white mb-1">{carbonData.totalEmissions.toFixed(2)}</div>
                <div className="text-xs text-muted-foreground font-medium">kg CO2e</div>
              </div>
            </div>
            <div className="relative overflow-hidden bg-gradient-to-br from-ring/20 via-card to-background rounded-xl border border-ring/30 p-6 shadow-xl hover:shadow-ring/20 transition-all duration-300 transform hover:scale-105">
              <div className="absolute top-0 right-0 w-32 h-32 bg-ring/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-ring rounded-full animate-pulse"></div>
                  <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Total Orders</div>
                </div>
                <div className="text-4xl font-bold text-white mb-1">{carbonData.totalOrders}</div>
                <div className="text-xs text-muted-foreground font-medium">jobs completed</div>
              </div>
            </div>
            <div className="relative overflow-hidden bg-gradient-to-br from-brand/20 via-card to-background rounded-xl border border-brand/30 p-6 shadow-xl hover:shadow-brand/20 transition-all duration-300 transform hover:scale-105">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-brand rounded-full animate-pulse"></div>
                  <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Total Distance</div>
                </div>
                <div className="text-4xl font-bold text-white mb-1">{carbonData.totalDistance.toFixed(2)}</div>
                <div className="text-xs text-muted-foreground font-medium">kilometers</div>
              </div>
            </div>
          </div>

          {/* Download Button */}
          <div className="flex justify-end">
            <Button
              type="button"
              variant="default"
              size="lg"
              onClick={downloadReport}
              className="shadow-lg hover:shadow-success/50 transform hover:scale-105 active:scale-95"
            >
              <Download className="w-5 h-5" />
              Download Report (CSV)
            </Button>
          </div>

          {/* Client Breakdown Table */}
          <div className="bg-gradient-to-br from-card to-background rounded-xl border border-border/50 overflow-hidden shadow-xl">
            <div className="p-5 border-b border-border/50 bg-gradient-to-r from-card/50 to-background/50">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-success" />
                Emissions by Client
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background/80 backdrop-blur-sm">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground uppercase tracking-wider">Client</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-foreground uppercase tracking-wider">Orders</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-foreground uppercase tracking-wider">Distance (km)</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-foreground uppercase tracking-wider">Emissions (kg CO2e)</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-foreground uppercase tracking-wider">Avg per Order</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {(() => {
                    const startIndex = (clientPage - 1) * clientRowsPerPage;
                    const endIndex = startIndex + clientRowsPerPage;
                    const paginatedClients = carbonData.byClient.slice(startIndex, endIndex);
                    
                    return (
                      <>
                        {paginatedClients.map((client, idx) => (
                          <tr key={idx} className="hover:bg-secondary/30 transition-colors duration-150">
                            <td className="px-6 py-4 text-white font-medium">{client.client}</td>
                            <td className="px-6 py-4 text-white text-right">{client.orders}</td>
                            <td className="px-6 py-4 text-white text-right">{client.distance.toFixed(2)}</td>
                            <td className="px-6 py-4 text-white text-right font-semibold text-success">{client.emissions.toFixed(2)}</td>
                            <td className="px-6 py-4 text-white text-right text-foreground">
                              {client.orders > 0 ? (client.emissions / client.orders).toFixed(2) : '0.00'}
                            </td>
                          </tr>
                        ))}
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
            {/* Pagination Controls for Client Table */}
            {carbonData.byClient.length > 0 && (() => {
              const totalPages = Math.ceil(carbonData.byClient.length / clientRowsPerPage);
              const startIndex = (clientPage - 1) * clientRowsPerPage;
              const endIndex = startIndex + clientRowsPerPage;
              
              return (
                <div className="flex items-center justify-between px-6 py-4 border-t border-border/50">
                  {/* Left side: Results count and rows per page */}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>
                      Showing {startIndex + 1}-{Math.min(endIndex, carbonData.byClient.length)} of {carbonData.byClient.length} results
                    </span>
                    <span className="text-accent">•</span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs">Show</span>
                      <select
                        className="bg-secondary/80 border border-accent rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-2 focus:ring-success focus:border-success"
                        value={clientRowsPerPage}
                        onChange={(e) => {
                          setClientRowsPerPage(Number(e.target.value));
                          setClientPage(1);
                        }}
                      >
                        {[10, 25, 50, 100].map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <span className="text-xs">per page</span>
                    </div>
                  </div>
                  {/* Right side: Page navigation */}
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="hover:text-white hover:bg-secondary/50"
                        disabled={clientPage === 1}
                        onClick={() => setClientPage(clientPage - 1)}
                        title="Previous page"
                      >
                        &lt;
                      </Button>
                      <span className="text-sm text-foreground min-w-20 text-center">
                        Page {clientPage}/{totalPages}
                      </span>
                      <input
                        type="number"
                        min={1}
                        max={totalPages}
                        value={clientPage}
                        onChange={(e) => {
                          let val = Number(e.target.value);
                          if (val < 1) val = 1;
                          if (val > totalPages) val = totalPages;
                          setClientPage(val);
                        }}
                        className="w-12 text-center bg-secondary/80 border border-accent rounded px-2 py-1 text-sm text-white focus:outline-none focus:ring-2 focus:ring-success focus:border-success"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="hover:text-white hover:bg-secondary/50"
                        disabled={clientPage === totalPages}
                        onClick={() => setClientPage(clientPage + 1)}
                        title="Next page"
                      >
                        &gt;
                      </Button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Job Breakdown Table */}
          <div className="bg-gradient-to-br from-card to-background rounded-xl border border-border/50 overflow-hidden shadow-xl">
            <div className="p-5 border-b border-border/50 bg-gradient-to-r from-card/50 to-background/50">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-success" />
                Emissions by Job
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background/80 backdrop-blur-sm">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground uppercase tracking-wider">Job ID</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground uppercase tracking-wider">Job Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground uppercase tracking-wider">Client</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-foreground uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-foreground uppercase tracking-wider">Distance (km)</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-foreground uppercase tracking-wider">Emissions (kg CO2e)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {(() => {
                    const startIndex = (jobPage - 1) * jobRowsPerPage;
                    const endIndex = startIndex + jobRowsPerPage;
                    const paginatedJobs = carbonData.byJob.slice(startIndex, endIndex);
                    
                    return (
                      <>
                        {paginatedJobs.map((job, idx) => (
                          <tr key={idx} className="hover:bg-secondary/30 transition-colors duration-150">
                            <td className="px-6 py-4 text-white font-mono text-sm text-ring">{job.jobId}</td>
                            <td className="px-6 py-4 text-white font-medium">{job.jobName}</td>
                            <td className="px-6 py-4 text-white">{job.client}</td>
                            <td className="px-6 py-4 text-white text-foreground">{job.date}</td>
                            <td className="px-6 py-4 text-white text-right">{job.distance.toFixed(2)}</td>
                            <td className="px-6 py-4 text-white text-right font-semibold text-success">{job.emissions.toFixed(2)}</td>
                          </tr>
                        ))}
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
            {/* Pagination Controls for Job Table */}
            {carbonData.byJob.length > 0 && (() => {
              const totalPages = Math.ceil(carbonData.byJob.length / jobRowsPerPage);
              const startIndex = (jobPage - 1) * jobRowsPerPage;
              const endIndex = startIndex + jobRowsPerPage;
              
              return (
                <div className="flex items-center justify-between px-6 py-4 border-t border-border/50">
                  {/* Left side: Results count and rows per page */}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>
                      Showing {startIndex + 1}-{Math.min(endIndex, carbonData.byJob.length)} of {carbonData.byJob.length} results
                    </span>
                    <span className="text-accent">•</span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs">Show</span>
                      <select
                        className="bg-secondary/80 border border-accent rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-2 focus:ring-success focus:border-success"
                        value={jobRowsPerPage}
                        onChange={(e) => {
                          setJobRowsPerPage(Number(e.target.value));
                          setJobPage(1);
                        }}
                      >
                        {[10, 25, 50, 100].map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <span className="text-xs">per page</span>
                    </div>
                  </div>
                  {/* Right side: Page navigation */}
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="hover:text-white hover:bg-secondary/50"
                        disabled={jobPage === 1}
                        onClick={() => setJobPage(jobPage - 1)}
                        title="Previous page"
                      >
                        &lt;
                      </Button>
                      <span className="text-sm text-foreground min-w-20 text-center">
                        Page {jobPage}/{totalPages}
                      </span>
                      <input
                        type="number"
                        min={1}
                        max={totalPages}
                        value={jobPage}
                        onChange={(e) => {
                          let val = Number(e.target.value);
                          if (val < 1) val = 1;
                          if (val > totalPages) val = totalPages;
                          setJobPage(val);
                        }}
                        className="w-12 text-center bg-secondary/80 border border-accent rounded px-2 py-1 text-sm text-white focus:outline-none focus:ring-2 focus:ring-success focus:border-success"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="hover:text-white hover:bg-secondary/50"
                        disabled={jobPage === totalPages}
                        onClick={() => setJobPage(jobPage + 1)}
                        title="Next page"
                      >
                        &gt;
                      </Button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16 bg-gradient-to-br from-card/50 to-background/50 rounded-xl border border-border/50">
          <Loader2 className="w-12 h-12 animate-spin text-success mb-4 drop-shadow-glow-success-lg" />
          <span className="text-foreground font-medium">Loading carbon data...</span>
          <span className="text-muted-foreground text-sm mt-2">Fetching orders from Qargo export</span>
        </div>
      )}

      {/* Empty State - No data loaded yet */}
      {!loading && !error && !carbonData && orders.length === 0 && (
        <div className="text-center py-16 bg-gradient-to-br from-card/50 to-background/50 rounded-xl border border-border/50">
          <div className="w-16 h-16 bg-secondary/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="text-foreground font-medium text-lg mb-2">No orders loaded</p>
          <p className="text-muted-foreground text-sm mb-4">Click "Refresh Data" to fetch orders from Qargo export.</p>
          <Button
            type="button"
            variant="default"
            size="lg"
            onClick={fetchOrders}
            className="shadow-lg hover:shadow-success/50 transform hover:scale-105"
          >
            Load Orders
          </Button>
        </div>
      )}

      {/* Empty State - Data loaded but no results */}
      {!loading && !error && carbonData && carbonData.totalOrders === 0 && (
        <div className="text-center py-16 bg-gradient-to-br from-card/50 to-background/50 rounded-xl border border-border/50">
          <div className="w-16 h-16 bg-secondary/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="text-foreground font-medium text-lg mb-2">No orders found</p>
          <p className="text-muted-foreground text-sm">No orders match the selected time period and filters.</p>
          <p className="text-muted-foreground text-xs mt-2">Try selecting a different time period or adjusting filters.</p>
        </div>
      )}

      {/* Debug info - remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <div className="bg-background rounded-lg border border-border p-4 text-xs text-muted-foreground">
          <p>Debug: Orders: {orders.length}, Clients: {clients.length}, Jobs: {jobs.length}, Carbon Data: {carbonData ? 'Yes' : 'No'}, Loading: {loading ? 'Yes' : 'No'}, Error: {error || 'None'}</p>
        </div>
      )}
    </div>
  );
}

